import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Box,
  TextField,
  MenuItem,
  Stack,
  Menu,
  ListItemIcon,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import OpacityIcon from "@mui/icons-material/Opacity";
import AirIcon from "@mui/icons-material/Air";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GridOnIcon from "@mui/icons-material/GridOn";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useSimulations, useDeleteSimulation, useSimulationProgress, useStartSimulation, useRerunSimulation, useUpdateSimulationName, useCurrentSimulationResults, useSimulationIsCurrent } from "../../api/simulations";
import SimulationProgressModal from "./SimulationProgressModal";
import RerunMissingDataModal, { ConstantWeather, RerunMissingField, RerunMissingValues } from "./RerunMissingDataModal";
import { useCuringEstimate } from "../../api/curing";
import { FireSimulation } from "../../types/FireSimulation";
import { useSimulationGroups, useCreateSimulationGroup, useRenameSimulationGroup, useDeleteSimulationGroup, useUpdateSimulationGroup } from "../../api/simulationGroups";
import { useIgnitionRegimes } from "../../api/ignitionRegimes";
import { useSimulation, SimulationParams, EnsembleConfig } from "../../contexts/SimulationContext";
import { SpottingMode, SPOTTING_MODE_LABELS } from "../../types/spotting";
import EnsembleConfigModal from "../EnsembleConfigModal";
import {useDate} from "../../contexts/DateContext";
import { StrategyContext } from "../../contexts/StrategyContext";
import { IgnitionRegimeContext } from "../../contexts/IgnitionRegimeContext";
import { useTimezone } from "../../contexts/TimezoneContext";
import { useHideLayerPanel } from "../../contexts/LayerPanelContext";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";
import WeatherProbeModal from "../weather/WeatherProbeModal";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import { formatValueWithUnit, toDisplayTemperature, toDisplaySpeed, formatDroughtIndex, droughtIndexScaleLabel } from "../../utils/units";
import { formatInTimezone, parseInTimezone } from "../../utils/datetime";
import { isForecastWeatherLabel, parseWeatherGridSuffix, stripWeatherGridSuffix, weatherGridShapeLabel } from "../../utils/weatherGrid";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { Collapse } from "@mui/material";

// Component to display weather charts in expanded row

/**
 * Where a simulation's curing value came from. Worth showing: a satellite
 * measurement and a typed-in guess carry very different weight when someone
 * is deciding whether to trust the run.
 */
const curingSourceLabel = (source: string): string => {
  if (source === 'estimate') return 'satellite';
  if (source === 'agent') return 'assistant';
  if (source === 'default') return 'default';
  return 'set by hand';
};

const curingSourceTooltip = (source: string): string => {
  if (source === 'estimate') return 'Measured from satellite imagery of this project.';
  if (source === 'agent') return 'Estimated by the AI assistant from fuel dryness.';
  if (source === 'default') return 'No measurement was available, so the platform default was used.';
  return 'Entered by a person for this run.';
};

const WeatherChartsRow: React.FC<{
  weather: any;
  colSpan: number;
  unitSystem: 'metric' | 'imperial';
  droughtFactor?: number | null;
  droughtIndex?: number | null;
  curing?: number | null;
  curingSource?: string | null;
  timezone?: string;
}> = ({ weather, colSpan, unitSystem, droughtFactor, droughtIndex, curing, curingSource, timezone }) => {
  if (!weather || typeof weather !== 'object') {
    return (
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: 'none' }}>
          <Collapse in timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 3 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                No weather data available
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  }

  const formatHourLabel = (isoKey: string): string => {
    try {
      const d = dayjs(isoKey);
      if (!d.isValid()) return isoKey;
      if (timezone) {
        const utcMs = d.valueOf();
        const tzDate = new Date(utcMs);
        const formatted = tzDate.toLocaleString('en-AU', {
          timeZone: timezone,
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        return formatted;
      }
      return d.format('MMM D HH:mm');
    } catch {
      return isoKey;
    }
  };

  // Transform weather data into chart format, using real timestamps for x-axis
  const chartData = Object.entries(weather)
    .sort(([hourA], [hourB]) => new Date(hourA).getTime() - new Date(hourB).getTime())
    .map(([hour, data]: [string, any]) => {
      if (!data || typeof data !== 'object') return null;
      return {
        hourLabel: formatHourLabel(hour),
        temperature: typeof data.temperature === 'number' 
          ? toDisplayTemperature(data.temperature, unitSystem)
          : null,
        windSpeed: typeof data.wind_speed === 'number'
          ? toDisplaySpeed(data.wind_speed, unitSystem)
          : null,
        windDirection: typeof data.wind_direction === 'number' ? data.wind_direction : null,
        humidity: typeof data.relative_humidity === 'number'
          ? data.relative_humidity
          : null,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const tempUnit = unitSystem === 'imperial' ? '°F' : '°C';
  const speedUnit = unitSystem === 'imperial' ? 'mph' : 'km/h';

  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: '1px solid rgba(255, 255, 255, 0.06)', bgcolor: 'rgba(0, 0, 0, 0.2)' }}>
        <Collapse in timeout="auto" unmountOnExit>
          <Box sx={{ py: 3, px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                Weather Conditions Over Time
              </Typography>
              {droughtFactor != null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, borderRadius: 2, bgcolor: 'rgba(255, 165, 0, 0.15)', border: '1px solid rgba(255, 165, 0, 0.3)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 165, 0, 0.9)', fontWeight: 500 }}>Drought Factor</Typography>
                  <Typography variant="caption" sx={{ color: '#ffa500', fontWeight: 700 }}>{droughtFactor}/10</Typography>
                </Box>
              )}
              {droughtIndex != null && (
                <Tooltip title={`Keetch-Byram Drought Index: how dry the deep soil and litter are (${droughtIndexScaleLabel(unitSystem)})`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, borderRadius: 2, bgcolor: 'rgba(255, 165, 0, 0.15)', border: '1px solid rgba(255, 165, 0, 0.3)' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 165, 0, 0.9)', fontWeight: 500 }}>KBDI</Typography>
                    <Typography variant="caption" sx={{ color: '#ffa500', fontWeight: 700 }}>{formatDroughtIndex(droughtIndex, unitSystem)}</Typography>
                  </Box>
                </Tooltip>
              )}
              {curing != null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, borderRadius: 2, bgcolor: 'rgba(144, 238, 144, 0.15)', border: '1px solid rgba(144, 238, 144, 0.3)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(144, 238, 144, 0.9)', fontWeight: 500 }}>Grass Curing</Typography>
                  <Typography variant="caption" sx={{ color: '#90ee90', fontWeight: 700 }}>{curing}%</Typography>
                  {curingSource && (
                    <Tooltip title={curingSourceTooltip(curingSource)}>
                      <Typography
                        variant="caption"
                        sx={{ color: 'rgba(144, 238, 144, 0.65)', fontWeight: 500 }}
                      >
                        {curingSourceLabel(curingSource)}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
              {/* Temperature Chart */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
                  Temperature ({tempUnit})
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="hourLabel" 
                      stroke="rgba(255, 255, 255, 0.5)"
                      tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 11 }}
                    />
                    <YAxis 
                      stroke="rgba(255, 255, 255, 0.5)"
                      tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1a1d2e', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 4 }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#ff6b6b' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="temperature" 
                      stroke="#ff6b6b" 
                      strokeWidth={2}
                      dot={{ fill: '#ff6b6b', r: 3 }}
                      activeDot={{ r: 5 }}
                      name="Temperature"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              {/* Wind Speed Chart */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
                  Wind Speed ({speedUnit})
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData} margin={{ top: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="hourLabel"
                      stroke="rgba(255, 255, 255, 0.5)"
                      tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 11 }}
                    />
                    <YAxis 
                      stroke="rgba(255, 255, 255, 0.5)"
                      tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1a1d2e', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 4 }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#4dabf7' }}
                      formatter={(value: any, _name: string, props: any) => {
                        const dir = props.payload?.windDirection;
                        if (dir !== null && dir !== undefined) {
                          return [`${value} ${speedUnit} · ${dir}°`, 'Wind Speed'];
                        }
                        return [value, 'Wind Speed'];
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="windSpeed" 
                      stroke="#4dabf7" 
                      strokeWidth={2}
                      dot={(props: any) => {
                        const { cx, cy, payload } = props;
                        const dir = payload?.windDirection;
                        const rad = dir !== null && dir !== undefined
                          ? ((180 + dir) * Math.PI) / 180
                          : null;
                        return (
                          <g key={`dot-${cx}-${cy}`}>
                            <circle cx={cx} cy={cy} r={3} fill="#4dabf7" />
                            {rad !== null && (
                              <g transform={`translate(${cx}, ${cy - 12}) rotate(${180 + (dir ?? 0)}, 0, 0)`}>
                                <line x1={0} y1={-6} x2={0} y2={6} stroke="rgba(255,255,255,0.7)" strokeWidth={1.5} strokeLinecap="round" />
                                <polygon points="0,-7 -3,0 3,0" fill="rgba(255,255,255,0.7)" />
                              </g>
                            )}
                          </g>
                        );
                      }}
                      activeDot={{ r: 5 }}
                      name="Wind Speed"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              {/* Humidity Chart */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
                  Relative Humidity (%)
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="hourLabel" 
                      stroke="rgba(255, 255, 255, 0.5)"
                      tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 11 }}
                    />
                    <YAxis 
                      stroke="rgba(255, 255, 255, 0.5)"
                      tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: '#1a1d2e', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 4 }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#51cf66' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="humidity" 
                      stroke="#51cf66" 
                      strokeWidth={2}
                      dot={{ fill: '#51cf66', r: 3 }}
                      activeDot={{ r: 5 }}
                      name="Humidity"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

// Shows is_current status for the single currently-loaded simulation only
const LoadedSimCurrentChip: React.FC<{ simulationId: string | null }> = ({ simulationId }) => {
  const { data: isCurrentData, isLoading } = useSimulationIsCurrent(simulationId);

  if (!simulationId) return null;
  if (isLoading) return (
    <Chip label="Checking..." size="small" sx={{ fontSize: '0.75rem', backgroundColor: 'rgba(255, 255, 255, 0.08)', color: 'rgba(255, 255, 255, 0.5)' }} />
  );

  const isCurrent = isCurrentData?.data?.is_current ?? false;
  return (
    <Tooltip
      title={isCurrent
        ? "This simulation matches the current mitigation strategy configuration"
        : "The mitigation strategy has been modified since this simulation was run"}
      arrow
      placement="bottom"
    >
      <Chip
        label={isCurrent ? "Current" : "Modified"}
        size="small"
        sx={{
          fontSize: '0.75rem',
          fontWeight: isCurrent ? 500 : 400,
          backgroundColor: isCurrent ? 'rgba(0, 212, 173, 0.2)' : 'rgba(244, 67, 54, 0.2)',
          color: isCurrent ? '#00d4ad' : '#ef5350'
        }}
      />
    </Tooltip>
  );
};

// Baselines for ETA extrapolation, keyed by simulation id. Kept at module
// scope so a row remount (group collapse, modal close/reopen) doesn't restart
// the clock while a run continues in the background.
const etaBaselines = new Map<string, { t0: number; p0: number }>();

const formatEta = (seconds: number, percent: number): string => {
  // Round coarsely early in the run while the extrapolation is least reliable
  const step = percent < 25 ? 60 : percent < 60 ? 30 : 10;
  const rounded = Math.max(step, Math.round(seconds / step) * step);
  const h = Math.floor(rounded / 3600);
  const m = Math.floor((rounded % 3600) / 60);
  const s = rounded % 60;
  if (h > 0) return `~${h}h ${m}m left`;
  if (m > 0) return s > 0 ? `~${m}m ${s}s left` : `~${m}m left`;
  return `~${s}s left`;
};

// Estimates time remaining by extrapolating % completed against time elapsed
// since progress was first observed, so the estimate sharpens as the run
// advances. Counts down locally between progress polls.
const useSimulationEta = (simulationId: string, percent: number, isActive: boolean): string | null => {
  const estimateRef = useRef<{ etaSeconds: number; at: number } | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!isActive) {
      etaBaselines.delete(simulationId);
      estimateRef.current = null;
      return;
    }
    if (percent <= 0 || percent >= 100) return;
    const now = Date.now();
    const base = etaBaselines.get(simulationId);
    if (!base || percent < base.p0) {
      etaBaselines.set(simulationId, { t0: now, p0: percent });
      return;
    }
    const elapsed = (now - base.t0) / 1000;
    const advanced = percent - base.p0;
    if (elapsed < 3 || advanced <= 0) return;
    estimateRef.current = { etaSeconds: (elapsed * (100 - percent)) / advanced, at: now };
  }, [simulationId, percent, isActive]);

  // Re-render each second so the countdown stays live between progress polls
  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isActive]);

  if (!isActive || !estimateRef.current) return null;
  const remaining = estimateRef.current.etaSeconds - (Date.now() - estimateRef.current.at) / 1000;
  if (remaining <= 5) return 'almost done';
  return formatEta(remaining, percent);
};

// Human labels for the backend's preparation stages (fallbacks when the
// progress payload has no prep_detail string).
const PREP_STAGE_LABELS: Record<string, string> = {
  queued: 'Waiting for a simulation worker',
  loading_layers: 'Loading project layers',
  preparing_rasters: 'Preparing terrain and fuel rasters',
  uploading_manifest: 'Uploading simulation inputs',
  enqueueing_tasks: 'Enqueueing simulation tasks',
  done: 'Preparation complete',
};

// Preparation stages heartbeat at least once per fuel tile / SQS batch; a
// quiet spell this long means the prep task has likely stalled or died.
const PREP_STALE_AFTER_MS = 3 * 60 * 1000;

// After the tasks are enqueued, ECS has to scale simulation workers up from
// (possibly) zero before any ignition starts. That routinely takes a few
// minutes; only warn once it exceeds this.
const WORKER_START_SLOW_AFTER_MS = 10 * 60 * 1000;

const formatDurationShort = (ms: number): string => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

// Re-render every second while active so elapsed/staleness readouts stay live
// between progress polls.
const useNowTick = (active: boolean): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

// Component to handle progress display for individual simulations
const SimulationProgressCell: React.FC<{
  simulationId: string;
  onDelete: () => void;
  onRerun?: () => void;
  hideDelete?: boolean;
  isDeleting?: boolean;
  onToggleExpand?: () => void;
  isExpanded?: boolean;
  hasWeatherData?: boolean;
  onShowProgress?: () => void;
  onMenuOpenChange?: (open: boolean) => void;
}> = ({ simulationId, onDelete, onRerun, hideDelete = false, isDeleting = false, onToggleExpand, isExpanded = false, hasWeatherData = false, onShowProgress, onMenuOpenChange }) => {
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const openMenu = (el: HTMLElement) => { setMenuAnchor(el); onMenuOpenChange?.(true); };
  const closeMenu = () => { setMenuAnchor(null); onMenuOpenChange?.(false); };
  const { data: progressData } = useSimulationProgress(simulationId);

  // Treat missing data as running (pending) so UI doesn't show action buttons prematurely
  const status = progressData?.status;
  const percent = progressData?.progress ?? 0;
  const isRunning = status !== 'SUCCESS' && status !== 'FAILURE';
  const isCollating = status === 'COLLATING';
  const isPreparing = status === 'PREPARING';
  // Preparation done and tasks enqueued, but no worker has picked one up yet
  // (ECS scaling up from zero): every ignition is still QUEUED.
  const ignitions = progressData?.ignitions ?? [];
  const isStartingWorkers =
    status === 'PENDING' &&
    ignitions.length > 0 &&
    ignitions.every((ig) => ig.status === 'QUEUED');
  const eta = useSimulationEta(
    simulationId,
    percent,
    isRunning && !isCollating && !isPreparing && !isStartingWorkers && !isDeleting
  );
  const now = useNowTick((isPreparing || isStartingWorkers) && !isDeleting);

  // Background deletion in progress: show status, hide all actions.
  if (isDeleting) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, width: '100%' }}>
        <CircularProgress size={14} sx={{ color: '#ef5350' }} />
        <Typography variant="body2" sx={{ color: '#ef5350' }}>Deleting…</Typography>
      </Box>
    );
  }

  // Preparing: the prep task (worker spin-up, raster assembly, S3 upload,
  // job enqueue) hasn't finished yet. Show its live stage detail, elapsed
  // time since kickoff, and a warning if its heartbeat has gone quiet.
  if (isPreparing) {
    const detail =
      progressData?.prep_detail ||
      PREP_STAGE_LABELS[progressData?.prep_stage ?? ''] ||
      'Preparing simulation';
    const createdMs = progressData?.created_at ? Date.parse(progressData.created_at) : NaN;
    const heartbeatMs = progressData?.prep_heartbeat_at ? Date.parse(progressData.prep_heartbeat_at) : NaN;
    const elapsed = Number.isFinite(createdMs) ? formatDurationShort(now - createdMs) : null;
    const sinceHeartbeat = Number.isFinite(heartbeatMs) ? now - heartbeatMs : null;
    const isStale = sinceHeartbeat !== null && sinceHeartbeat > PREP_STALE_AFTER_MS;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box sx={{ width: '100%', minWidth: 110, mr: 1 }}>
          <LinearProgress
            variant="indeterminate"
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#ffb74d',
                borderRadius: 5,
              }
            }}
          />
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.25, color: isStale ? '#ffb74d' : 'rgba(255, 255, 255, 0.5)' }}
          >
            {isStale
              ? `${detail} · no updates for ${formatDurationShort(sinceHeartbeat!)}`
              : `${detail}${elapsed ? ` · ${elapsed}` : ''}`}
          </Typography>
        </Box>
        {onShowProgress && (
          <Tooltip title="View preparation details">
            <IconButton
              onClick={onShowProgress}
              size="small"
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff9800' } }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!hideDelete && !viewOnly && (
          <IconButton
            onClick={onDelete}
            size="small"
            sx={{ color: '#ef5350' }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    );
  }

  // Tasks enqueued but no worker has started an ignition yet — compute is
  // scaling up. Without this branch the row sits on a misleading bare "0%".
  if (isStartingWorkers) {
    const enqueuedMs = progressData?.prep_heartbeat_at ? Date.parse(progressData.prep_heartbeat_at) : NaN;
    const sinceEnqueue = Number.isFinite(enqueuedMs) ? Math.max(0, now - enqueuedMs) : null;
    const isSlow = sinceEnqueue !== null && sinceEnqueue > WORKER_START_SLOW_AFTER_MS;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box sx={{ width: '100%', minWidth: 110, mr: 1 }}>
          <LinearProgress
            variant="indeterminate"
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#ffb74d',
                borderRadius: 5,
              }
            }}
          />
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.25, color: isSlow ? '#ffb74d' : 'rgba(255, 255, 255, 0.5)' }}
          >
            {isSlow
              ? `Workers are taking longer than usual to start · ${formatDurationShort(sinceEnqueue!)}`
              : `Starting simulation workers…${sinceEnqueue !== null ? ` · ${formatDurationShort(sinceEnqueue)}` : ''}`}
          </Typography>
        </Box>
        {onShowProgress && (
          <Tooltip title="View preparation details">
            <IconButton
              onClick={onShowProgress}
              size="small"
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff9800' } }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!hideDelete && !viewOnly && (
          <IconButton
            onClick={onDelete}
            size="small"
            sx={{ color: '#ef5350' }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    );
  }

  if (isRunning) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {isCollating ? (
          // Show "Collating..." with indeterminate progress when collating
          <>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress
                variant="indeterminate"
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#ff6b35',
                    borderRadius: 5,
                  }
                }}
              />
            </Box>
            <Box sx={{ minWidth: 70, mr: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Collating...
              </Typography>
            </Box>
          </>
        ) : (
          // Show progress percentage when running
          <Box sx={{ width: '100%', minWidth: 90, mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ff6b35',
                  borderRadius: 5,
                }
              }}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'rgba(255, 255, 255, 0.5)', whiteSpace: 'nowrap' }}>
              {`${Math.round(percent)}%`}{eta ? ` · ${eta}` : ''}
            </Typography>
          </Box>
        )}
        {onShowProgress && (
          <Tooltip title="View ignition details">
            <IconButton
              onClick={onShowProgress}
              size="small"
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff9800' } }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!hideDelete && !viewOnly && (
          <IconButton
            onClick={onDelete}
            size="small"
            sx={{ color: '#ef5350' }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    );
  }

  // Show action buttons when not running
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
      {hasWeatherData && onToggleExpand && (
        <IconButton
          onClick={onToggleExpand}
          size="small"
          sx={{ color: '#4dabf7' }}
        >
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      )}
      {!viewOnly && (
        <>
          <IconButton
            onClick={(e) => openMenu(e.currentTarget)}
            size="small"
            aria-label="More actions"
            sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            PaperProps={{ sx: { bgcolor: '#1f2232', color: '#fff', minWidth: 180 } }}
          >
            {onRerun && (
              <MenuItem onClick={() => { closeMenu(); onRerun(); }}>
                <ListItemIcon><RefreshIcon fontSize="small" sx={{ color: '#4dabf7' }} /></ListItemIcon>
                Rerun simulation
              </MenuItem>
            )}
            <MenuItem onClick={() => { closeMenu(); onDelete(); }} sx={{ color: '#ef5350' }}>
              <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef5350' }} /></ListItemIcon>
              Delete simulation
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

// For ensemble runs the backend prepends "Burned area (P50)" / "Impacted assets
// (P50)" KPIs alongside the base "Average ha burnt" / "Average impacted assets
// per ignition" ones, which renders as near-duplicate columns. Fold each pair
// into a single canonical column; when both variants are present the CI-bearing
// ensemble KPI wins so the cell can show the full spread.
const KPI_KEY_ALIASES: Record<string, string> = {
  // Current ensemble labels (per-ignition averages, matching single runs)
  'avg_ha_burnt_(p50)': 'burned_area',
  'avg_impacted_assets_(p50)': 'impacted_assets',
  // Legacy ensemble labels (member totals) — old records may still carry them
  'burned_area_(p50)': 'burned_area',
  'impacted_assets_(p50)': 'impacted_assets',
  // Single-run labels
  'average_ha_burnt': 'burned_area',
  'average_impacted_assets_per_ignition': 'impacted_assets',
};

const KPI_LABEL_OVERRIDES: Record<string, string> = {
  burned_area: 'Avg Burned Area',
  impacted_assets: 'Avg Impacted Assets',
};

// Canonical KPI key -> field in sim.ensemble_stats holding the member distribution
const ENSEMBLE_STATS_FIELDS: Record<string, string> = {
  burned_area: 'burned_area_ha',
  impacted_assets: 'impacted_assets',
};

const kpiSafeKey = (label: any): string =>
  typeof label === 'string'
    ? label.toLowerCase().replace(/\s+/g, '_')
    : `kpi_${Math.random().toString(36).substring(2, 9)}`;

const kpiColumnKey = (label: any): string => {
  const safeKey = kpiSafeKey(label);
  return KPI_KEY_ALIASES[safeKey] ?? safeKey;
};

const kpiColumnLabel = (key: string, label: string): string => KPI_LABEL_OVERRIDES[key] ?? label;

// Table layout. The table is wider than the dialog once a few KPI columns
// exist, so the body scrolls sideways; the Name column is pinned to the left
// and the Actions column to the right so a row stays identifiable and
// operable at any scroll position. Pinned cells need an opaque background
// (row tints are translucent), hence the solid blends below.
const ROW_BG = '#1a1d2e';
const ROW_BG_ALT = '#1f2232';        // ROW_BG + 2% white
const ROW_BG_HOVER = '#28222e';      // ROW_BG + 6% orange
const GROUP_ROW_BG = '#252838';      // ROW_BG + 5% white
const HEADER_CELL_SX = {
  whiteSpace: 'normal',
  lineHeight: 1.3,
  bgcolor: ROW_BG,
  color: 'rgba(255, 255, 255, 0.45)',
  borderBottom: '2px solid rgba(255, 255, 255, 0.08)',
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  py: 1.5,
} as const;
const STICKY_LEFT_SX = { position: 'sticky', left: 0, zIndex: 1 } as const;
const STICKY_RIGHT_SX = { position: 'sticky', right: 0, zIndex: 1 } as const;
const NAME_COL_SX = { minWidth: 200, maxWidth: 280 } as const;
const TEXT_COL_SX = { minWidth: 140, maxWidth: 200 } as const;

// Clicking a row loads that simulation. Controls inside the row (buttons,
// chips, the rename field, menus) keep their own meaning, and a drag that
// selected text is not a click.
const isRowClickSwallowed = (e: React.MouseEvent): boolean => {
  const target = e.target as HTMLElement | null;
  if (target?.closest?.('button, a, input, textarea, [role="button"], [role="menuitem"], .MuiChip-root')) return true;
  return (window.getSelection?.()?.toString() ?? '') !== '';
};

// Two-line clamp with the full text on hover, for the long strategy/regime names
const ClampedText: React.FC<{ text: string; muted?: boolean }> = ({ text, muted }) => (
  <Tooltip title={text} arrow placement="top" enterDelay={400}>
    <Box
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        lineHeight: 1.35,
        color: muted ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)',
      }}
    >
      {text}
    </Box>
  </Tooltip>
);

const kpiHasCi = (kpi: any): boolean =>
  kpi?.ci_low !== null && kpi?.ci_low !== undefined && kpi?.ci_high !== null && kpi?.ci_high !== undefined;

// Collapse a sim's KPI list to one entry per canonical column
const canonicalizeSimKpis = (kpis: any): Map<string, any> => {
  const map = new Map<string, any>();
  if (Array.isArray(kpis)) {
    kpis.forEach((kpi: any) => {
      if (kpi && typeof kpi === 'object' && kpi.label) {
        const key = kpiColumnKey(kpi.label);
        const existing = map.get(key);
        if (!existing || (kpiHasCi(kpi) && !kpiHasCi(existing))) {
          map.set(key, kpi);
        }
      }
    });
  }
  return map;
};

// Renders KPI cells for a simulation row; replaces them with a large Failed chip when the simulation has failed
const SimulationKpiOrFailedCells: React.FC<{
  simulationId: string;
  sim: any;
  allKpiKeys: string[];
  unitSystem: 'metric' | 'imperial';
}> = ({ simulationId, sim, allKpiKeys, unitSystem }) => {
  const { data: progressData } = useSimulationProgress(simulationId);
  const isFailed = progressData?.status === 'FAILURE';

  const formatKpi = (value: any, units?: string) => {
    if (value === null || value === undefined) return 'N/A';
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(num)) return formatValueWithUnit(num, units, unitSystem);
    const safeUnits = typeof units === 'string' && units.trim() ? ` ${units}` : '';
    return `${String(value)}${safeUnits}`;
  };

  if (isFailed) {
    return (
      <TableCell
        colSpan={Math.max(allKpiKeys.length, 1)}
        sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5 }}
      >
        <Chip
          label="Failed"
          sx={{
            height: '28px',
            fontSize: '0.8rem',
            fontWeight: 700,
            backgroundColor: 'rgba(239, 83, 80, 0.15)',
            color: '#ef5350',
            border: '1px solid rgba(239, 83, 80, 0.3)',
          }}
        />
      </TableCell>
    );
  }

  if (allKpiKeys.length === 0) {
    return (
      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.4)', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, fontSize: '0.8rem', fontStyle: 'italic' }}>
        No KPI data available
      </TableCell>
    );
  }

  const simKpiMap = canonicalizeSimKpis(sim.kpis);

  return (
    <>
      {allKpiKeys.map((kpiKey) => {
        if (!simKpiMap.has(kpiKey)) {
          return (
            <TableCell key={kpiKey} sx={{ color: 'rgba(255, 255, 255, 0.3)', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, fontSize: '0.8rem' }}>N/A</TableCell>
          );
        }
        const kpi = simKpiMap.get(kpiKey);
        // Ensemble KPIs carry 90% CI bounds; render the P50 with the full member spread below it.
        const hasCi = kpiHasCi(kpi);
        const statsField = ENSEMBLE_STATS_FIELDS[kpiKey];
        const stats = hasCi && statsField ? sim.ensemble_stats?.[statsField] : undefined;
        return (
          <TableCell key={kpiKey} sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, fontSize: '0.8rem' }}>
            {formatKpi(kpi.value, kpi.units)}
            {hasCi && (
              <Typography component="div" sx={{ fontSize: '0.66rem', color: '#a78bfa', mt: 0.25 }}>
                P50 · 90% CI {formatKpi(kpi.ci_low, kpi.units)}–{formatKpi(kpi.ci_high, kpi.units)}
              </Typography>
            )}
            {stats && (
              <Typography component="div" sx={{ fontSize: '0.66rem', color: 'rgba(255, 255, 255, 0.45)', mt: 0.25 }}>
                mean {formatKpi(stats.mean, kpi.units)} · range {formatKpi(stats.min, kpi.units)}–{formatKpi(stats.max, kpi.units)}
              </Typography>
            )}
          </TableCell>
        );
      })}
    </>
  );
};

// Django serializes DurationField as "HH:MM:SS" or "D HH:MM:SS"
const durationToHours = (duration?: string | null): number | null => {
  if (!duration) return null;
  const match = duration.match(/^(?:(\d+)\s+)?(\d+):(\d+):(\d+)$/);
  if (!match) return null;
  const days = match[1] ? parseInt(match[1], 10) : 0;
  const hours = parseInt(match[2], 10);
  if (isNaN(days) || isNaN(hours)) return null;
  const total = days * 24 + hours;
  return total > 0 ? total : null;
};

const hasHourlyWeather = (weather: unknown): boolean =>
  !!weather && typeof weather === 'object' && Object.keys(weather).length > 0;

// Expand user-supplied constant conditions into the hourly weather object the
// backend expects, covering every hour of the run.
const buildConstantWeather = (
  conditions: ConstantWeather,
  startIso: string,
  durationHours: number,
): Record<string, ConstantWeather> => {
  const start = dayjs(startIso).startOf('hour');
  const weather: Record<string, ConstantWeather> = {};
  for (let h = 0; h <= durationHours; h++) {
    weather[start.add(h, 'hour').format('YYYY-MM-DDTHH:mm:ssZ')] = { ...conditions };
  }
  return weather;
};

// Run parameters a legacy record may have been saved without; each one gets an
// input in the missing-data modal before the rerun can start.
const getRerunMissingFields = (sim: FireSimulation): RerunMissingField[] => {
  const missing: RerunMissingField[] = [];
  if (!sim.start_time && !sim.start_date_time) missing.push('startTime');
  if (durationToHours(sim.duration) === null) missing.push('duration');
  if (sim.drought_factor === null || sim.drought_factor === undefined) missing.push('droughtFactor');
  if (sim.curing === null || sim.curing === undefined) missing.push('curing');
  if (sim.ignition_regime_id === null || sim.ignition_regime_id === undefined) missing.push('ignitionRegime');
  if (!hasHourlyWeather(sim.weather)) missing.push('weather');
  return missing;
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const SavedSimulationsModal: React.FC<Props> = ({ open, onClose }) => {
  useHideLayerPanel(open);
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNumber = Number(projectId) || 0;
  const unitSystem = useUnitSystem();
  const { selectedTimezone } = useTimezone();

  const {
    simulationId,
    currentSavedSimId,
    setCurrentSavedSimId,
    setIsSimulationComplete,
    setSimulationId,
    simulationParams,
    setSimulationParams,
  } = useSimulation();

  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  // Poll the simulations list while a simulation is running so the new row appears ASAP
  const pollRunning = !!simulationId && open;
  const { data, isLoading, isFetching, refetch } = useSimulations(pollRunning);
  // The list polls every 2s while any sim is in a transitional state
  // (PREPARING/PENDING/DELETING — see useSimulations); treat that like an
  // active run so the background-refetch overlay below doesn't flash the
  // table on every poll.
  const anyTransitional = (data?.data || []).some(
    (s: any) => s.status === 'DELETING' || s.status === 'PREPARING' || s.status === 'PENDING'
  );
  const deleteMutation = useDeleteSimulation();
  const updateNameMutation = useUpdateSimulationName();
  const { data: groupsData, isFetching: isFetchingGroups, isLoading: isLoadingGroups } = useSimulationGroups();
  const createGroupMutation = useCreateSimulationGroup();
  const renameGroupMutation = useRenameSimulationGroup();
  const deleteGroupMutation = useDeleteSimulationGroup();
  const updateSimGroupMutation = useUpdateSimulationGroup();
  const { selectedDate, setSelectedDate } = useDate();
  const { selectedStrategy, setSelectedStrategy } = useContext(StrategyContext);
  const { selectedRegime, setSelectedRegime } = useContext(IgnitionRegimeContext);
  const { ignitionRegimes } = useIgnitionRegimes(projectIdNumber);
  // Seed a rerun's curing from the project's satellite reading rather than a
  // fixed number, so a rerun starts from a measurement where one exists.
  const { data: curingData } = useCuringEstimate(projectId);
  const suggestedCuring =
    curingData?.for_date?.curing ?? curingData?.for_date?.suggested ?? null;

  // Track current simulation results loading state
  const { isLoading: isSimulationResultsLoading } = useCurrentSimulationResults();

  // State for confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    newDate: dayjs.Dayjs | null;
    newStrategyId: string | null;
    newStrategyName: string | null;
    newRegimeId: number | null;
    simId: string;
    changes: string[];
  }>({ open: false, newDate: null, newStrategyId: null, newStrategyName: null, newRegimeId: null, simId: '', changes: [] });

  // State for generate simulation modal
  const [generateModalOpen, setGenerateModalOpen] = useState(false);

  // State for the ensemble configuration step shown after the weather modal.
  const [ensembleModalOpen, setEnsembleModalOpen] = useState(false);
  const [pendingSimParams, setPendingSimParams] = useState<SimulationParams | null>(null);
  const [ensembleSubmitting, setEnsembleSubmitting] = useState(false);

  const [regimeRequiredOpen, setRegimeRequiredOpen] = useState(false);

  const [pendingRegimeId, setPendingRegimeId] = useState<number | null>(null);

  // State for editing simulation names
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  const [progressModalSimId, setProgressModalSimId] = useState<string | null>(null);

  // State for loading simulation
  const [loadingSimulationId, setLoadingSimulationId] = useState<string | null>(null);

  // State for expanded simulation rows
  const [expandedSimulations, setExpandedSimulations] = useState<Set<string>>(new Set());

  // Group UI state
  const [expandedGroups, setExpandedGroups] = useState<Set<number | 'ungrouped'>>(new Set());
  // Stable color per group id for chips – bright colors readable on dark bg
  const groupChipColors = [
    { bg: 'rgba(96, 165, 250, 0.2)', text: '#60a5fa', dot: '#60a5fa' },   // blue
    { bg: 'rgba(251, 146, 60, 0.2)', text: '#fb923c', dot: '#fb923c' },   // orange
    { bg: 'rgba(52, 211, 153, 0.2)', text: '#34d399', dot: '#34d399' },   // emerald
    { bg: 'rgba(244, 114, 182, 0.2)', text: '#f472b6', dot: '#f472b6' },  // pink
    { bg: 'rgba(250, 204, 21, 0.2)', text: '#facc15', dot: '#facc15' },   // yellow
    { bg: 'rgba(167, 139, 250, 0.2)', text: '#a78bfa', dot: '#a78bfa' },  // violet
    { bg: 'rgba(45, 212, 191, 0.2)', text: '#2dd4bf', dot: '#2dd4bf' },   // teal
  ] as const;
  const groupChipColorForId = (groupId: number) => {
    const idx = Math.abs(groupId) % groupChipColors.length;
    return groupChipColors[idx];
  };
  useEffect(() => {
    if (groupsData?.data) {
      const next = new Set<number | 'ungrouped'>(['ungrouped' as any]);
      groupsData.data.forEach((g: any) => next.add(g.id));
      setExpandedGroups(next);
    }
  }, [groupsData]);

  const toggleGroup = (groupKey: number | 'ungrouped') => {
    setExpandedGroups((prev) => {
      const n = new Set(prev);
      if (n.has(groupKey)) n.delete(groupKey);
      else n.add(groupKey);
      return n;
    });
  };

  // Expand/Collapse all helpers
  const expandAllGroups = () => {
    const all = new Set<number | 'ungrouped'>(['ungrouped' as any]);
    (groupsData?.data || []).forEach((g: any) => all.add(g.id));
    setExpandedGroups(all);
  };
  const collapseAllGroups = () => setExpandedGroups(new Set());

  // Polished dialogs for group create/rename/delete
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createGroupName, setCreateGroupName] = useState("");
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameGroupId, setRenameGroupId] = useState<number | null>(null);
  const [renameGroupName, setRenameGroupName] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState<number | null>(null);
  const [deleteSimDialogOpen, setDeleteSimDialogOpen] = useState(false);
  const [deleteSimTarget, setDeleteSimTarget] = useState<{ id: string; name: string } | null>(null);

  // Group actions menu state
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(null);
  const [groupMenuGroupId, setGroupMenuGroupId] = useState<number | null>(null);
  const openGroupMenu = (anchor: HTMLElement, groupId: number) => {
    setGroupMenuAnchor(anchor);
    setGroupMenuGroupId(groupId);
  };
  const closeGroupMenu = () => {
    setGroupMenuAnchor(null);
    setGroupMenuGroupId(null);
  };

  // Sim row: group picker menu state
  const [simGroupMenuAnchor, setSimGroupMenuAnchor] = useState<null | HTMLElement>(null);
  const [simGroupMenuSimId, setSimGroupMenuSimId] = useState<string | null>(null);
  // Row clicks load a simulation, but a click that merely dismisses an
  // in-progress interaction (renaming, a group menu, a row's actions menu)
  // must not. The name field saves on blur before the click arrives, so the
  // state is snapshotted at mousedown rather than read at click time.
  const [actionMenuSimId, setActionMenuSimId] = useState<string | null>(null);
  const interactionActive = editingName !== null || groupMenuAnchor !== null || simGroupMenuAnchor !== null || actionMenuSimId !== null;
  const interactionActiveRef = useRef(false);
  interactionActiveRef.current = interactionActive;
  const dismissClickRef = useRef(false);
  const openSimGroupMenu = (anchor: HTMLElement, simId: string) => {
    setSimGroupMenuAnchor(anchor);
    setSimGroupMenuSimId(simId);
  };
  const closeSimGroupMenu = () => {
    setSimGroupMenuAnchor(null);
    setSimGroupMenuSimId(null);
  };

  const openCreateDialog = () => {
    setCreateGroupName("");
    setCreateDialogOpen(true);
  };
  const handleCreateGroup = async () => {
    const name = createGroupName.trim();
    if (!name) return;
    try {
      await createGroupMutation.mutateAsync(name);
      setCreateDialogOpen(false);
      setCreateGroupName("");
    } catch (e) {
      console.error('Failed to create group', e);
    }
  };

  const handleRenameGroup = (groupId: number, currentName: string) => {
    setRenameGroupId(groupId);
    setRenameGroupName(currentName);
    setRenameDialogOpen(true);
  };
  const confirmRenameGroup = async () => {
    if (renameGroupId == null) return;
    const name = renameGroupName.trim();
    if (!name) return;
    try {
      await renameGroupMutation.mutateAsync({ groupId: renameGroupId, name });
      setRenameDialogOpen(false);
    } catch (e) {
      console.error('Failed to rename group', e);
    }
  };

  const handleDeleteGroup = (groupId: number) => {
    setDeleteGroupId(groupId);
    setDeleteDialogOpen(true);
  };
  const confirmDeleteGroup = async () => {
    if (deleteGroupId == null) return;
    try {
      await deleteGroupMutation.mutateAsync(deleteGroupId);
      setDeleteDialogOpen(false);
    } catch (e) {
      console.error('Failed to delete group', e);
    }
  };

  const handleChangeSimGroup = async (simulationId: string, group: number | 'ungrouped') => {
    try {
      await updateSimGroupMutation.mutateAsync({ simulationId, group: group === 'ungrouped' ? null : (group as number) });
    } catch (e) {
      console.error('Failed to update simulation group', e);
    }
  };

  // Pre-compute KPI columns for consistent layout and group header colSpan
  const allKpiColumns = useMemo(() => {
    const all = new Map<string, string>();
    if (data?.data) {
      data.data.forEach((sim: any) => {
        if (Array.isArray(sim.kpis)) {
          sim.kpis.forEach((kpi: any) => {
            if (kpi && typeof kpi === 'object' && kpi.label) {
              const key = kpiColumnKey(kpi.label);
              if (!all.has(key)) {
                all.set(key, kpiColumnLabel(key, kpi.label as string));
              }
            }
          });
        }
      });
    }
    return Array.from(all.entries());
  }, [data]);
  const allKpiKeys = useMemo(() => allKpiColumns.map(([key]) => key), [allKpiColumns]);
  const totalTableColumns = 8 + allKpiKeys.length;

  const ignitionRegimeNameById = useMemo(() => {
    const map = new Map<number, string>();
    (ignitionRegimes || []).forEach((r) => map.set(r.id, r.name));
    return map;
  }, [ignitionRegimes]);

  // No column tracking needed as we handle KPI data robustness inline

  // Helper function to format KPI values
  const formatKpiValue = (value: any, units?: string) => {
    if (value === null || value === undefined) return "N/A";

    const numericValue = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(numericValue)) {
      return formatValueWithUnit(numericValue, units, unitSystem);
    }

    const safeUnits = typeof units === "string" && units.trim() ? ` ${units}` : "";
    return `${String(value)}${safeUnits}`;
  };

  // Refresh list whenever modal opens
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  // Clear loading state when API call completes
  useEffect(() => {
    // Clear local loading state when modal closes or when not loading API data
    if (loadingSimulationId && !open) {
      setLoadingSimulationId(null);
    }
  }, [loadingSimulationId, open]);

  useEffect(() => {
    if (pendingRegimeId === null) return;
    const nextRegime = ignitionRegimes?.find((r) => r.id === pendingRegimeId) ?? null;
    if (!nextRegime) return;
    setSelectedRegime(nextRegime);
    setPendingRegimeId(null);
  }, [pendingRegimeId, ignitionRegimes, setSelectedRegime]);

  // Close modal when API call completes
  useEffect(() => {
    if (loadingSimulationId && !isSimulationResultsLoading) {
      // API call has completed, close the modal
      onClose();
      setLoadingSimulationId(null);
    }
  }, [loadingSimulationId, isSimulationResultsLoading, onClose]);

  const handleLoad = (simId: string, simStartTime?: string) => {
    // Find the simulation data to get the strategy ID and name
    const simulation = data?.data?.find((sim: any) => sim.simulation_id === simId);
    const simStrategyId = simulation?.mitigation_strategy;
    const simStrategyName = simulation?.mitigation_strategy_name;
    const simRegimeId: number | null = simulation?.ignition_regime_id ?? null;
    const currentStrategyId = selectedStrategy?.id;
    const currentStrategyName = selectedStrategy?.name;
    const currentRegimeId = selectedRegime?.id ?? null;

    const changes: string[] = [];
    let newDate: dayjs.Dayjs | null = null;
    let newStrategyId: string | null = null;
    let newStrategyName: string | null = null;
    let newRegimeId: number | null = null;

    // Check if the simulation's date differs from the current selected date
    if (simStartTime) {
      const simDate = parseInTimezone(simStartTime, selectedTimezone) ?? dayjs(simStartTime);
      const currentDate = selectedDate;

      // Compare just the date part (ignore time)
      if (!simDate.isSame(currentDate, 'day')) {
        changes.push(`Switch date from ${currentDate.format('YYYY-MM-DD')} to ${simDate.format('YYYY-MM-DD')}`);
        newDate = simDate;
      }
    }

    // Check if the simulation's strategy differs from the current selected strategy (compare by ID)
    if (simStrategyId && currentStrategyId && simStrategyId !== currentStrategyId) {
      changes.push(`Switch strategy from "${currentStrategyName || 'Current Strategy'}" to "${simStrategyName || 'Unknown Strategy'}"`);
      newStrategyId = simStrategyId || null;
      newStrategyName = simStrategyName || null;
    }

    if (simRegimeId !== null && simRegimeId !== currentRegimeId) {
      const simRegimeName = ignitionRegimes?.find((r) => r.id === simRegimeId)?.name;
      const currentRegimeName = selectedRegime?.name;
      changes.push(`Switch ignition regime from "${currentRegimeName || 'None'}" to "${simRegimeName || `#${simRegimeId}`}"`);
      newRegimeId = simRegimeId;
    }

    // If there are changes needed, show confirmation dialog
    if (changes.length > 0) {
      setConfirmDialog({
        open: true,
        newDate,
        newStrategyId,
        newStrategyName,
        newRegimeId,
        simId: simId,
        changes
      });
      return;
    }

    if (newRegimeId !== null) {
      const nextRegime = ignitionRegimes?.find((r) => r.id === newRegimeId) ?? null;
      if (nextRegime) setSelectedRegime(nextRegime);
      else setPendingRegimeId(newRegimeId);
    }

    // If no changes needed, proceed with loading
    loadSimulation(simId);
  };

  const loadSimulation = async (simId: string) => {
    setLoadingSimulationId(simId);
    try {
      // mark this as the loaded simulation
      setCurrentSavedSimId(simId);
      setIsSimulationComplete(true);
      setSimulationId(null);

      // Don't close the modal immediately - let the spinner show and close when API completes
      // The modal will be closed via the effect when the API call completes
    } catch (error) {
      console.error('Failed to load simulation:', error);
      setLoadingSimulationId(null);
    }
  };

  const handleConfirmChanges = async () => {
    // Apply date change if needed
    if (confirmDialog.newDate) {
      setSelectedDate(confirmDialog.newDate);
    }

    // Apply strategy change if needed
    if (confirmDialog.newStrategyId && confirmDialog.newStrategyName) {
      // Create strategy object using the ID from the simulation
      const newStrategy = {
        id: confirmDialog.newStrategyId,
        name: confirmDialog.newStrategyName
      };
      setSelectedStrategy(newStrategy as any); // temporary type assertion - should be proper Strategy type
    }

    if (confirmDialog.newRegimeId !== null) {
      const nextRegime = ignitionRegimes?.find((r) => r.id === confirmDialog.newRegimeId) ?? null;
      if (nextRegime) setSelectedRegime(nextRegime);
      else setPendingRegimeId(confirmDialog.newRegimeId);
    }

    await loadSimulation(confirmDialog.simId);
    setConfirmDialog({ open: false, newDate: null, newStrategyId: null, newStrategyName: null, newRegimeId: null, simId: '', changes: [] });
  };

  const handleCancelChanges = () => {
    setConfirmDialog({ open: false, newDate: null, newStrategyId: null, newStrategyName: null, newRegimeId: null, simId: '', changes: [] });
  };

  const requestDeleteSim = (id: string, name?: string) => {
    setDeleteSimTarget({ id, name: name || 'Unnamed Simulation' });
    setDeleteSimDialogOpen(true);
  };

  const confirmDeleteSim = async () => {
    if (!deleteSimTarget) return;
    const { id } = deleteSimTarget;
    try {
      await deleteMutation.mutateAsync(id);
      // Clear simulationId context if we're deleting the currently tracked simulation
      if (simulationId === id) {
        setSimulationId(null);
      }
    } catch (error) {
      console.error('Failed to delete simulation:', error);
      // You might want to show a toast/alert here
    } finally {
      setDeleteSimDialogOpen(false);
      setDeleteSimTarget(null);
    }
  };

  const rerunMutation = useRerunSimulation();
  const [rerunTarget, setRerunTarget] = useState<FireSimulation | null>(null);
  const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false);
  const [rerunMissingOpen, setRerunMissingOpen] = useState(false);

  const requestRerunSim = (sim: FireSimulation) => {
    setRerunTarget(sim);
    setRerunConfirmOpen(true);
  };

  const cancelRerunSim = () => {
    setRerunConfirmOpen(false);
    setRerunMissingOpen(false);
    setRerunTarget(null);
  };

  // Rebuild the run parameters from the saved record, with any user-supplied
  // values for fields the record was missing taking precedence.
  const buildRerunParams = (sim: FireSimulation, values: RerunMissingValues): SimulationParams => {
    const durationHours = values.duration ?? durationToHours(sim.duration) ?? 8;
    const startIso = values.startTime ?? sim.start_time ?? sim.start_date_time;
    const savedPerturbations = sim.ensemble_perturbations ?? {};
    const ensemble: EnsembleConfig | null = sim.is_ensemble
      ? {
          enabled: true,
          members: sim.ensemble_members ?? sim.ensemble_stats?.requested_members ?? 2,
          perturbations: {
            weather: savedPerturbations.weather ?? { enabled: false, magnitudePct: 8 },
            fuel: savedPerturbations.fuel ?? { enabled: false, magnitudePct: 8 },
            topography: savedPerturbations.topography ?? { enabled: false, magnitudePct: 8 },
          },
        }
      : null;
    return {
      simulationType: sim.weather_label === 'Custom weather' ? 'DEFINE_CUSTOM_WEATHER' : 'USE_WEATHER_FORECAST',
      // The missing-data modal guarantees values for anything the record lacks,
      // so the record/values pair always yields a number here.
      droughtFactor: (values.droughtFactor ?? sim.drought_factor)!,
      curing: (values.curing ?? sim.curing)!,
      duration: durationHours,
      interval: (durationHours * 60) / 240,
      startTime: dayjs(startIso).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      timezone: selectedTimezone,
      weather: values.constantWeather
        ? buildConstantWeather(values.constantWeather, startIso, durationHours)
        : sim.weather ?? {},
      fireSpreadModel: 'New',
      droughtIndex: sim.drought_index ?? null,
      spottingMode: sim.spotting_mode ?? 'realistic',
      ignitionRegimeId: values.ignitionRegimeId ?? sim.ignition_regime_id ?? null,
      // The listing omits `weather_grid`, so a rerun goes out uniform (the
      // backend treats an absent grid as today's single-point weather); the
      // label loses its grid suffix so the new row doesn't claim one.
      weatherLabel: sim.weather_label ? stripWeatherGridSuffix(sim.weather_label) : undefined,
      ensemble,
    };
  };

  const startRerun = async (params: SimulationParams) => {
    if (!rerunTarget) return;
    try {
      const newSimId = await rerunMutation.mutateAsync({
        // Rerun under the strategy the simulation was originally run with
        strategyId: rerunTarget.mitigation_strategy ?? selectedStrategy?.id,
        params,
      });
      setSimulationId(newSimId);
      cancelRerunSim();
      // Same refetch cadence as a fresh run so the new row surfaces promptly
      await refetch();
      setTimeout(() => { refetch(); }, 1000);
      setTimeout(() => { refetch(); }, 3000);
    } catch (error) {
      console.error('Failed to rerun simulation:', error);
    }
  };

  // The user has confirmed the rerun; start it straight away unless the record
  // predates some of the run parameters, in which case collect them first.
  const confirmRerunSim = async () => {
    if (!rerunTarget) return;
    if (getRerunMissingFields(rerunTarget).length > 0) {
      setRerunConfirmOpen(false);
      setRerunMissingOpen(true);
      return;
    }
    await startRerun(buildRerunParams(rerunTarget, {}));
  };

  const startSimulationMutation = useStartSimulation();

  const handleNewSimulation = () => {
    if (!selectedRegime || selectedRegime.ignition_count === 0) {
      setRegimeRequiredOpen(true);
      return;
    }
    setGenerateModalOpen(true);
  };

  // Called when the user clicks "Generate" in the weather modal. Instead of
  // starting the run immediately, capture the freshly-built weather params and
  // hand off to the ensemble configuration step.
  const handleGenerateSimulation = (params?: SimulationParams) => {
    setPendingSimParams(params ?? simulationParams ?? null);
    setGenerateModalOpen(false);
    setEnsembleModalOpen(true);
  };

  // Kick off the run once the ensemble has been configured. A null config means
  // the user chose a plain single run. The spotting mode rides along inside the
  // params built by the weather modal's run views.
  const startConfiguredSimulation = async (ensemble: EnsembleConfig | null) => {
    setEnsembleSubmitting(true);
    try {
      const finalParams: SimulationParams = {
        ...(pendingSimParams ?? (simulationParams as SimulationParams)),
        ensemble: ensemble && ensemble.enabled ? ensemble : null,
      };
      // Persist so the rest of the app sees the ensemble config too.
      setSimulationParams(finalParams);
      const newSimId = await startSimulationMutation.mutateAsync(finalParams);
      // Store the running simulation id so other components can react
      setSimulationId(newSimId);
      setEnsembleModalOpen(false);
      setPendingSimParams(null);
      // Initial refetch to try to surface the new row
      await refetch();
      // Extra refetches to account for slight async lag in record creation
      setTimeout(() => { refetch(); }, 1000);
      setTimeout(() => { refetch(); }, 3000);
    } catch (error) {
      console.error('Failed to start simulation:', error);
    } finally {
      setEnsembleSubmitting(false);
    }
  };

  const handleCancelGenerate = () => {
    setGenerateModalOpen(false);
  };

  const handleCancelEnsemble = () => {
    setEnsembleModalOpen(false);
    setPendingSimParams(null);
  };

  const handleEditName = (simulationId: string, currentName: string) => {
    setEditingName(simulationId);
    setEditingValue(currentName || 'Unnamed Simulation');
  };

  const handleSaveName = async (simulationId: string) => {
    try {
      await updateNameMutation.mutateAsync({
        simulationId,
        name: editingValue
      });

      // Exit editing mode
      setEditingName(null);
      setEditingValue('');
    } catch (error) {
      console.error('Failed to update simulation name:', error);
      // You might want to show a toast/alert here
    }
  };

  const handleCancelEdit = () => {
    setEditingName(null);
    setEditingValue('');
  };

  const toggleSimulationExpand = (simulationId: string) => {
    setExpandedSimulations((prev) => {
      const next = new Set(prev);
      if (next.has(simulationId)) {
        next.delete(simulationId);
      } else {
        next.add(simulationId);
      }
      return next;
    });
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff', borderRadius: 2, width: 'calc(100% - 48px)', m: 3 } }}>
      <DialogTitle sx={{ m: 0, px: 2.5, py: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', fontWeight: 700, fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
          Saved Simulations
          <LoadedSimCurrentChip simulationId={currentSavedSimId ?? null} />
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: 'rgba(255, 255, 255, 0.7)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Loading Overlay */}
      {(loadingSimulationId || isSimulationResultsLoading) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 17, 23, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            flexDirection: 'column',
            gap: 2
          }}
        >
          <CircularProgress size={48} sx={{ color: '#ff6b35' }} />
          <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Loading Simulation...
          </Typography>
        </Box>
      )}
      <DialogContent dividers sx={{ maxHeight: 'calc(var(--vh100) * 0.7)', overflow: 'auto', p: 0, position: 'relative', borderColor: 'rgba(255, 255, 255, 0.1)', '&::-webkit-scrollbar': { width: 8, height: 8 }, '&::-webkit-scrollbar-track': { bgcolor: '#0f1117' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 4, '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } } }}>
        {/* Top toolbar: counts + actions */}
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, backgroundColor: '#151828', zIndex: 1 }}>
          <Stack direction="row" spacing={3}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Groups: {(groupsData?.data?.length ?? 0) + 1}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Simulations: {data?.data?.length ?? 0}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button onClick={expandAllGroups} size="small" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Expand All</Button>
            <Button onClick={collapseAllGroups} size="small" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Collapse All</Button>
            <Button onClick={openCreateDialog} variant="outlined" size="small" startIcon={<AddIcon />} sx={{ color: '#ff6b35', borderColor: '#ff6b35', '&:hover': { borderColor: '#ff7f50', bgcolor: 'rgba(255, 107, 53, 0.1)' } }}>New Group</Button>
          </Stack>
        </Box>
        {isLoading || isLoadingGroups ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#ff6b35' }} />
          </Box>
        ) : ((data?.data?.length ?? 0) > 0 || (!!simulationId && open)) ? (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEADER_CELL_SX, ...STICKY_LEFT_SX, ...NAME_COL_SX, zIndex: 3 }}>Name</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX }}>Date Created</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX }}>Start Time</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX }}>Duration</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, ...TEXT_COL_SX }}>Strategy</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, ...TEXT_COL_SX }}>Ignition Regime</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, minWidth: 150 }}>Weather Summary</TableCell>
                {/* Dynamic KPI headers */}
                {allKpiColumns.map(([safeKey, label]) => (
                    <TableCell
                      key={safeKey}
                      sx={{
                        whiteSpace: 'normal',
                        lineHeight: 1.3,
                        width: '110px',
                        maxWidth: '130px',
                        minWidth: '96px',
                        bgcolor: ROW_BG,
                        color: 'rgba(255, 255, 255, 0.45)',
                        borderBottom: '2px solid rgba(255, 255, 255, 0.08)',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        py: 1.5
                      }}
                    >
                      {label}
                    </TableCell>
                ))}
                <TableCell align="right" sx={{ ...HEADER_CELL_SX, ...STICKY_RIGHT_SX, zIndex: 3, boxShadow: '-6px 0 8px -6px rgba(0,0,0,0.5)' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* A newly started simulation appears as a real (PREPARING)
                  row: the backend persists the record at kickoff, so no
                  client-side placeholder is needed — the row survives
                  refreshes and is visible to every viewer. */}
              {(() => {
                const groupsList = groupsData?.data ?? [];
                const groupOrder: Array<{ id: number | 'ungrouped'; name: string }> = [
                  { id: 'ungrouped', name: 'Ungrouped' },
                  ...groupsList
                ] as any;
                return groupOrder.map((group: any) => {
                  const simsInGroup = (data?.data || []).filter((s: any) => (s.group ?? null) === (group.id === 'ungrouped' ? null : group.id));
                  if (simsInGroup.length === 0) return null;

                  // Calculate KPI averages for this group
                  const calculateGroupKpiAverages = () => {
                    const kpiAverages = new Map<string, { sum: number; count: number; units?: string; label: string }>();

                    simsInGroup.forEach((sim: any) => {
                      canonicalizeSimKpis(sim.kpis).forEach((kpi: any, safeKey: string) => {
                        if (typeof kpi.value === 'number' && isFinite(kpi.value)) {
                          if (!kpiAverages.has(safeKey)) {
                            kpiAverages.set(safeKey, { sum: 0, count: 0, units: kpi.units, label: kpiColumnLabel(safeKey, kpi.label) });
                          }

                          const existing = kpiAverages.get(safeKey)!;
                          existing.sum += kpi.value;
                          existing.count += 1;
                        }
                      });
                    });

                    // Convert to averages
                    const averages = new Map<string, { average: number; units?: string; label: string }>();
                    kpiAverages.forEach((value, key) => {
                      if (value.count > 0) {
                        averages.set(key, {
                          average: value.sum / value.count,
                          units: value.units,
                          label: value.label
                        });
                      }
                    });

                    return averages;
                  };

                  const groupKpiAverages = calculateGroupKpiAverages();

                  return (
                    <React.Fragment key={String(group.id)}>
                      <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: 52 }}>
                        <TableCell sx={{ ...STICKY_LEFT_SX, bgcolor: GROUP_ROW_BG, py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', borderLeft: '3px solid #ff6b35' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton size="small" onClick={() => toggleGroup(group.id)} sx={{ color: '#ff6b35' }}>
                              {expandedGroups.has(group.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <Typography variant="subtitle1" sx={{ ml: 1, color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{group.name} <Box component="span" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>({simsInGroup.length})</Box></Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}></TableCell>
                        <TableCell sx={{ py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}></TableCell>
                        <TableCell sx={{ py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}></TableCell>
                        <TableCell sx={{ py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}></TableCell>
                        <TableCell sx={{ py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}></TableCell>
                        <TableCell sx={{ py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}></TableCell>
                        {/* KPI Average columns */}
                        {allKpiKeys.map((kpiKey) => {
                          const avgData = groupKpiAverages.get(kpiKey);
                          return (
                            <TableCell key={`avg-${kpiKey}`} sx={{ py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              {avgData ? (
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff6b35', fontSize: '0.75rem' }}>
                                  {formatKpiValue(avgData.average, avgData.units)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.75rem' }}>
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ ...STICKY_RIGHT_SX, bgcolor: GROUP_ROW_BG, py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          {group.id !== 'ungrouped' && !viewOnly && (
                            <IconButton size="small" onClick={(e) => openGroupMenu(e.currentTarget, group.id as number)} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                              <MoreVertIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedGroups.has(group.id) && simsInGroup.map((sim: any, index: number) => {
                try {
                // Helper function to parse duration from Django/Python duration format
                const parseDuration = (duration: string) => {
                  if (!duration) return 'N/A';

                  // Check if it's a Django duration format with days (e.g., "1 00:00:00")
                  const dayMatch = duration.match(/^(\d+)\s+(\d+):(\d+):(\d+)$/);
                  if (dayMatch) {
                    const days = parseInt(dayMatch[1], 10);
                    const hours = parseInt(dayMatch[2], 10);
                    if (isNaN(days) || isNaN(hours)) return duration;
                    return `${days}d ${hours}h`;
                  }

                  // Otherwise, try standard HH:MM:SS format
                  const parts = duration.split(':');
                  if (parts.length !== 3) return duration;
                  const totalHours = parseInt(parts[0], 10);

                  if (isNaN(totalHours)) return duration;

                  // Convert to days if duration is 24 hours or more
                  if (totalHours >= 24) {
                    const days = Math.floor(totalHours / 24);
                    const remainingHours = totalHours % 24;
                    return `${days}d ${remainingHours}h`;
                  }

                  // Only show hours, not minutes (UI only allows full hours)
                  return `${totalHours}h`;
                };

                // Helper function to get weather summary with icons over 3 lines
                const getWeatherSummary = (weather: any) => {
                  if (!weather || typeof weather !== 'object') {
                    return (
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        N/A
                      </Typography>
                    );
                  }

                  const temps: number[] = [];
                  const windSpeeds: number[] = [];
                  const humidity: number[] = [];

                  Object.values(weather).forEach((hourData: any) => {
                    if (hourData && typeof hourData === 'object') {
                      if (typeof hourData.temperature === 'number') temps.push(hourData.temperature);
                      if (typeof hourData.wind_speed === 'number') windSpeeds.push(hourData.wind_speed);
                      if (typeof hourData.relative_humidity === 'number') humidity.push(hourData.relative_humidity);
                    }
                  });

                  return (
                    <div style={{ fontSize: '0.75rem', lineHeight: '1.2', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {temps.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                          <ThermostatIcon sx={{ fontSize: '20px', mr: '4px', color: '#ff6b6b' }} />
                          <span>
                            {toDisplayTemperature(Math.min(...temps), unitSystem).toFixed(0)}-
                            {toDisplayTemperature(Math.max(...temps), unitSystem).toFixed(0)}
                            {unitSystem === 'imperial' ? '°F' : '°C'}
                          </span>
                        </div>
                      )}
                      {windSpeeds.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                          <AirIcon sx={{ fontSize: '20px', mr: '4px', color: '#4dabf7' }} />
                          <span>
                            {(() => {
                              const min = toDisplaySpeed(Math.min(...windSpeeds), unitSystem).toFixed(0);
                              const max = toDisplaySpeed(Math.max(...windSpeeds), unitSystem).toFixed(0);
                              const unit = unitSystem === 'imperial' ? ' mph' : ' km/h';

                              return min === max ? `${min}${unit}` : `${min}-${max}${unit}`;
                            })()}
                          </span>
                        </div>
                      )}
                      {humidity.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <OpacityIcon sx={{ fontSize: '20px', mr: '4px', color: '#51cf66' }} />
                          <span>{Math.min(...humidity).toFixed(0)}-{Math.max(...humidity).toFixed(0)}% RH</span>
                        </div>
                      )}
                      {temps.length === 0 && windSpeeds.length === 0 && humidity.length === 0 && (
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          N/A
                        </Typography>
                      )}
                    </div>
                  );
                };


                // No need to extract keys since we're now dealing with an array of KPIs

                if (!sim || typeof sim !== 'object' || !sim.simulation_id) {
                  throw new Error('Simulation data missing required fields');
                }
                return (
                <React.Fragment key={sim.simulation_id}>
                <TableRow
                  hover
                  onMouseDown={() => { dismissClickRef.current = interactionActiveRef.current; }}
                  onClick={(e) => {
                    const dismissing = dismissClickRef.current;
                    dismissClickRef.current = false;
                    if (dismissing || sim.status !== 'SUCCESS' || isRowClickSwallowed(e)) return;
                    handleLoad(sim.simulation_id, sim.start_time);
                  }}
                  title={sim.status === 'SUCCESS' ? 'Click to load this simulation' : undefined}
                  sx={{ cursor: sim.status === 'SUCCESS' ? 'pointer' : 'default', bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)', '&:hover': { bgcolor: 'rgba(255, 107, 53, 0.06) !important' }, '& .pinned-cell': { bgcolor: index % 2 === 0 ? ROW_BG : ROW_BG_ALT }, '&:hover .pinned-cell': { bgcolor: ROW_BG_HOVER }, '& .row-edit': { opacity: 0 }, '&:hover .row-edit, & .row-edit:focus-visible': { opacity: 1 }, transition: 'background-color 0.15s ease' }}
                >
                  <TableCell className="pinned-cell" sx={{ ...STICKY_LEFT_SX, ...NAME_COL_SX, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5 }}>
                    {editingName === sim.simulation_id ? (
                      <TextField
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveName(sim.simulation_id);
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                        onBlur={() => handleSaveName(sim.simulation_id)}
                        autoFocus
                        size="small"
                        fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }, '&.Mui-focused fieldset': { borderColor: '#ff6b35' } } }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, padding: '4px 8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#fff', flex: 1 }}>
                            {sim.name || 'Unnamed Simulation'}
                          </Typography>
                          <Tooltip title="Rename" arrow>
                            <IconButton
                              className="row-edit"
                              size="small"
                              aria-label="Rename simulation"
                              onClick={() => handleEditName(sim.simulation_id, sim.name)}
                              sx={{ p: 0.25, color: 'rgba(255, 255, 255, 0.5)', transition: 'opacity 0.15s ease', '&:hover': { color: '#fff' } }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          {sim.is_ensemble && (
                            <Tooltip
                              title={(() => {
                                const base = `Ensemble of ${sim.ensemble_members ?? 0} members (same ignitions, perturbed inputs)`;
                                const ba = sim.ensemble_stats?.burned_area_ha;
                                if (!ba) return base;
                                const r = (n: number) => Math.round(n).toLocaleString();
                                return `${base} · Burned area P50 ${r(ba.p50)} ha (90% CI ${r(ba.p10)}–${r(ba.p90)})`;
                              })()}
                              arrow
                            >
                              <Chip
                                size="small"
                                label={`◇ Ensemble · ${sim.ensemble_members ?? 0}`}
                                sx={{
                                  height: '18px',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  backgroundColor: 'rgba(167, 139, 250, 0.18)',
                                  color: '#a78bfa',
                                  border: '1px solid rgba(167, 139, 250, 0.35)',
                                  '& .MuiChip-label': { px: 1, py: 0 },
                                }}
                              />
                            </Tooltip>
                          )}
                          {sim.spotting_mode && sim.spotting_mode !== 'realistic' && (
                            <Tooltip
                              title={sim.spotting_mode === 'off'
                                ? 'Ember spotting disabled for this run'
                                : 'Ember spotting distances reduced 10x for this run'}
                              arrow
                            >
                              <Chip
                                size="small"
                                label={`Spotting: ${SPOTTING_MODE_LABELS[sim.spotting_mode as SpottingMode]}`}
                                sx={{
                                  height: '18px',
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  '& .MuiChip-label': { px: 1, py: 0 },
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Box>
                          {(() => {
                            const currentGroupName = sim.group
                              ? ((groupsData?.data || []).find((g: any) => g.id === sim.group)?.name || 'Group')
                              : 'Ungrouped';
                            const isUngrouped = !sim.group;
                            const chipColors = isUngrouped
                              ? { bg: 'rgba(255, 255, 255, 0.08)', text: 'rgba(255, 255, 255, 0.6)' }
                              : groupChipColorForId(sim.group as number);
                            return (
                              <Tooltip title={currentGroupName} placement="top" arrow>
                                <Chip
                                  size="small"
                                  label={
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: 160 }}>
                                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {currentGroupName}
                                      </Box>
                                      <ArrowDropDownIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                  }
                                  onClick={(e) => openSimGroupMenu(e.currentTarget, sim.simulation_id)}
                                  sx={{
                                    height: 28,
                                    fontSize: '0.75rem',
                                    px: 1,
                                    cursor: 'pointer',
                                    maxWidth: 190,
                                    bgcolor: chipColors.bg,
                                    color: chipColors.text,
                                    border: isUngrouped ? '1px solid rgba(255, 255, 255, 0.15)' : 'none',
                                    '& .MuiChip-label': { px: 0 },
                                    '&:hover': { bgcolor: isUngrouped ? 'rgba(255, 255, 255, 0.12)' : chipColors.bg }
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {formatInTimezone(sim.created_at, selectedTimezone, "YYYY-MM-DD HH:mm")}
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {sim.start_time
                      ? formatInTimezone(sim.start_time, selectedTimezone, "YYYY-MM-DD HH:mm")
                      : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {parseDuration(sim.duration)}
                  </TableCell>
                  <TableCell sx={{ ...TEXT_COL_SX, borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, fontSize: '0.8rem' }}>
                    <ClampedText text={sim.mitigation_strategy_name || 'N/A'} muted={!sim.mitigation_strategy_name} />
                  </TableCell>
                  <TableCell sx={{ ...TEXT_COL_SX, borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, fontSize: '0.8rem' }}>
                    {sim.ignition_regime_id != null
                      ? <ClampedText text={ignitionRegimeNameById.get(sim.ignition_regime_id) || `#${sim.ignition_regime_id}`} />
                      : <ClampedText text="-" muted />}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5 }}>
                    {sim.weather_label && (
                      <Chip
                        label={sim.weather_label}
                        size="small"
                        sx={{
                          mb: 0.75,
                          display: 'flex',
                          maxWidth: 180,
                          bgcolor: isForecastWeatherLabel(sim.weather_label)
                            ? 'rgba(33, 150, 243, 0.15)'
                            : sim.weather_label === 'Custom weather'
                            ? 'rgba(156, 39, 176, 0.15)'
                            : 'rgba(76, 175, 80, 0.15)',
                          color: isForecastWeatherLabel(sim.weather_label)
                            ? '#64b5f6'
                            : sim.weather_label === 'Custom weather'
                            ? '#ce93d8'
                            : '#81c784',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                      />
                    )}
                    {(() => {
                      // Detail responses carry the grid itself; the listing only
                      // carries the label, whose suffix encodes the shape.
                      const shape = sim.weather_grid?.shape ?? parseWeatherGridSuffix(sim.weather_label);
                      if (!shape) return null;
                      const gridLabel = weatherGridShapeLabel({ shape });
                      return (
                        <Tooltip title={`Weather varies across project · ${gridLabel}`} arrow placement="top">
                          <Chip
                            icon={<GridOnIcon sx={{ fontSize: '0.85rem !important', color: '#80cbc4 !important' }} />}
                            label={gridLabel}
                            size="small"
                            sx={{
                              mb: 0.75,
                              height: 20,
                              bgcolor: 'rgba(128, 203, 196, 0.12)',
                              color: '#80cbc4',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                    {getWeatherSummary(sim.weather)}
                  </TableCell>
                  {/* Dynamic KPI columns */}
                  <SimulationKpiOrFailedCells
                    simulationId={sim.simulation_id}
                    sim={sim}
                    allKpiKeys={allKpiKeys}
                    unitSystem={unitSystem}
                  />
                  <TableCell align="right" className="pinned-cell" sx={{ ...STICKY_RIGHT_SX, borderBottom: '1px solid rgba(255, 255, 255, 0.06)', py: 1.5, boxShadow: '-6px 0 8px -6px rgba(0,0,0,0.5)' }}>
                    <SimulationProgressCell
                      simulationId={sim.simulation_id}
                      onDelete={() => requestDeleteSim(sim.simulation_id, sim.name)}
                      onRerun={() => requestRerunSim(sim)}
                      isDeleting={sim.status === 'DELETING'}
                      onToggleExpand={() => toggleSimulationExpand(sim.simulation_id)}
                      isExpanded={expandedSimulations.has(sim.simulation_id)}
                      hasWeatherData={!!(sim.weather && typeof sim.weather === 'object' && Object.keys(sim.weather).length > 0)}
                      onShowProgress={() => setProgressModalSimId(sim.simulation_id)}
                      onMenuOpenChange={(menuOpen) => setActionMenuSimId(menuOpen ? sim.simulation_id : null)}
                    />
                  </TableCell>
                </TableRow>
                {expandedSimulations.has(sim.simulation_id) && (
                  <WeatherChartsRow
                    weather={sim.weather}
                    colSpan={totalTableColumns}
                    unitSystem={unitSystem}
                    droughtFactor={sim.drought_factor}
                    droughtIndex={sim.drought_index}
                    curingSource={sim.curing_source}
                    curing={sim.curing}
                    timezone={selectedTimezone}
                  />
                )}
                </React.Fragment>
                );
                } catch (error) {
                  console.error('Failed to render simulation row due to missing data', sim, error);
                  const fallbackKey = sim?.simulation_id ?? `invalid-${group.id}-${index}`;
                  return (
                    <TableRow key={fallbackKey}>
                      <TableCell colSpan={Math.max(totalTableColumns, 1)} sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ color: '#ef5350' }}>
                            Fatal simulation error
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            We couldn't load this simulation because its data is missing or corrupt. Please delete it and try running the simulation again, or contact support if the issue persists.
                          </Typography>
                          {sim?.simulation_id && (
                            <Box>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => requestDeleteSim(sim.simulation_id, sim.name)}
                                sx={{ color: '#ef5350', borderColor: '#ef5350' }}
                              >
                                Delete simulation
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }
                      })}
                    </React.Fragment>
                  );
                });
              })()}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.5)', p: 4 }}>No saved simulations.</Typography>
        )}
        {/* Background refetch overlay (keeps table visible) */}
        {(isFetching || isFetchingGroups) && !pollRunning && !anyTransitional && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(15, 17, 23, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 5,
              pointerEvents: 'none'
            }}
          >
            <CircularProgress size={36} sx={{ color: '#ff6b35' }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', bgcolor: '#151828', px: 2.5, py: 1.5 }}>
        {!viewOnly && (
          <Button onClick={handleNewSimulation} variant="contained" sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' }, fontWeight: 600, px: 3, textTransform: 'none', boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)' }}>
            New Simulation
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
    <SimulationProgressModal
      open={!!progressModalSimId}
      onClose={() => setProgressModalSimId(null)}
      simulationId={progressModalSimId}
      simulationName={progressModalSimId ? (data?.data ?? []).find((s: any) => s.simulation_id === progressModalSimId)?.name : undefined}
    />
    {/* Group actions menu */}
    <Menu
      anchorEl={groupMenuAnchor}
      open={Boolean(groupMenuAnchor)}
      onClose={closeGroupMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { bgcolor: '#1a1d2e', border: '1px solid rgba(255, 255, 255, 0.1)' } } }}
    >
      <MenuItem
        onClick={() => {
          const current = (groupsData?.data || []).find((g: any) => g.id === groupMenuGroupId);
          if (current) {
            handleRenameGroup(current.id as number, current.name as string);
          }
          closeGroupMenu();
        }}
        sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
      >
        Rename Group
      </MenuItem>
      <MenuItem
        onClick={() => {
          if (groupMenuGroupId != null) handleDeleteGroup(groupMenuGroupId);
          closeGroupMenu();
        }}
        sx={{ color: '#ef5350', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
      >
        Delete Group
      </MenuItem>
    </Menu>

    {/* Simulation group selection menu (chip trigger) */}
    <Menu
      anchorEl={simGroupMenuAnchor}
      open={Boolean(simGroupMenuAnchor)}
      onClose={closeSimGroupMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { bgcolor: '#1a1d2e', border: '1px solid rgba(255, 255, 255, 0.1)' } } }}
    >
      {(() => {
        const simId = simGroupMenuSimId;
        const currentGroupValue = simId
          ? ((data?.data || []).find((s: any) => s.simulation_id === simId)?.group ?? 'ungrouped')
          : 'ungrouped';
        return (
          <>
            <MenuItem
              dense
              selected={currentGroupValue === 'ungrouped'}
              onClick={() => {
                if (simId) handleChangeSimGroup(simId, 'ungrouped');
                closeSimGroupMenu();
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, visibility: currentGroupValue === 'ungrouped' ? 'visible' : 'hidden', color: '#ff6b35' }}>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
              <Box component="span" sx={{ color: '#fff' }}>Ungrouped</Box>
            </MenuItem>
            {(groupsData?.data || []).map((g: any) => {
              const selected = currentGroupValue !== 'ungrouped' && currentGroupValue === g.id;
              const gColors = groupChipColorForId(g.id as number);
              return (
                <MenuItem
                  key={g.id}
                  dense
                  selected={selected}
                  onClick={() => {
                    if (simId) handleChangeSimGroup(simId, g.id);
                    closeSimGroupMenu();
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28, visibility: selected ? 'visible' : 'hidden', color: '#ff6b35' }}>
                    <CheckIcon fontSize="small" />
                  </ListItemIcon>
                  <Box sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: gColors.dot,
                    mr: 1
                  }} />
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', color: '#fff' }}>
                    {g.name}
                  </Box>
                </MenuItem>
              );
            })}
          </>
        );
      })()}
    </Menu>
    {/* Create Group Dialog */}
    <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}>
      <DialogTitle sx={{ color: '#fff' }}>New Group</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          autoFocus
          margin="dense"
          label="Group name"
          fullWidth
          size="small"
          value={createGroupName}
          onChange={(e) => setCreateGroupName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreateGroup(); }}
          sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }, '&.Mui-focused fieldset': { borderColor: '#ff6b35' } }, '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)', '&.Mui-focused': { color: '#ff6b35' } } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Cancel</Button>
        <Button onClick={handleCreateGroup} variant="contained" disabled={!createGroupName.trim()} sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' } }}>Create</Button>
      </DialogActions>
    </Dialog>

    {/* Rename Group Dialog */}
    <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}>
      <DialogTitle sx={{ color: '#fff' }}>Rename Group</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          autoFocus
          margin="dense"
          label="Group name"
          fullWidth
          size="small"
          value={renameGroupName}
          onChange={(e) => setRenameGroupName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') confirmRenameGroup(); }}
          sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }, '&.Mui-focused fieldset': { borderColor: '#ff6b35' } }, '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)', '&.Mui-focused': { color: '#ff6b35' } } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRenameDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Cancel</Button>
        <Button onClick={confirmRenameGroup} variant="contained" disabled={!renameGroupName.trim()} sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' } }}>Save</Button>
      </DialogActions>
    </Dialog>

    {/* Delete Group Confirmation */}
    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}>
      <DialogTitle sx={{ color: '#fff' }}>Delete Group</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Delete this group? Simulations will be left ungrouped.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Cancel</Button>
        <Button onClick={confirmDeleteGroup} variant="contained" sx={{ bgcolor: '#ef5350', '&:hover': { bgcolor: '#f44336' } }}>Delete</Button>
      </DialogActions>
    </Dialog>

    {/* Delete Simulation Confirmation */}
    <Dialog open={deleteSimDialogOpen} onClose={() => setDeleteSimDialogOpen(false)} PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}>
      <DialogTitle sx={{ color: '#fff' }}>Delete Simulation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Delete "{deleteSimTarget?.name}"? This permanently removes the simulation and its saved results, and cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteSimDialogOpen(false)} disabled={deleteMutation.isPending} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Cancel</Button>
        <Button onClick={confirmDeleteSim} variant="contained" disabled={deleteMutation.isPending} sx={{ bgcolor: '#ef5350', '&:hover': { bgcolor: '#f44336' } }}>{deleteMutation.isPending ? 'Deleting…' : 'Delete'}</Button>
      </DialogActions>
    </Dialog>

    {/* Rerun Simulation Confirmation */}
    <Dialog open={rerunConfirmOpen} onClose={rerunMutation.isPending ? undefined : cancelRerunSim} PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}>
      <DialogTitle sx={{ color: '#fff' }}>Rerun Simulation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          This will trigger the simulation to be rerun. Are you sure you wish to continue?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelRerunSim} disabled={rerunMutation.isPending} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Cancel</Button>
        <Button onClick={confirmRerunSim} variant="contained" disabled={rerunMutation.isPending} sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' } }}>{rerunMutation.isPending ? 'Starting…' : 'Rerun'}</Button>
      </DialogActions>
    </Dialog>

    {/* Collect run parameters a legacy simulation record is missing */}
    <RerunMissingDataModal
      open={rerunMissingOpen}
      simulationName={rerunTarget?.name || 'Unnamed Simulation'}
      missingFields={rerunTarget ? getRerunMissingFields(rerunTarget) : []}
      suggestedCuring={suggestedCuring}
      ignitionRegimes={(ignitionRegimes ?? []).map((r) => ({ id: r.id, name: r.name }))}
      submitting={rerunMutation.isPending}
      onCancel={cancelRerunSim}
      onConfirm={(values) => { if (rerunTarget) void startRerun(buildRerunParams(rerunTarget, values)); }}
    />

    {/* Changes Confirmation Dialog */}
    <Dialog open={confirmDialog.open} onClose={handleCancelChanges} PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}>
      <DialogTitle sx={{ color: '#fff' }}>Confirm Changes</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Loading this simulation will make the following changes to your environment:
        </Typography>
        <ul style={{ marginLeft: '16px', marginBottom: '16px' }}>
          {confirmDialog.changes.map((change, index) => (
            <li key={index}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>{change}</Typography>
            </li>
          ))}
        </ul>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Do you want to proceed?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelChanges} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Cancel
        </Button>
        <Button onClick={handleConfirmChanges} variant="contained" sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' } }}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>

    {/* Weather Probe Modal */}
    <WeatherProbeModal
      open={generateModalOpen}
      onClose={handleCancelGenerate}
      onGenerate={handleGenerateSimulation}
    />

    {/* Ensemble configuration — shown after Generate in the weather modal */}
    <EnsembleConfigModal
      open={ensembleModalOpen}
      ignitionCount={selectedRegime?.ignition_count ?? 0}
      regimeName={selectedRegime?.name}
      baseParams={pendingSimParams}
      onClose={handleCancelEnsemble}
      onConfirm={startConfiguredSimulation}
      submitting={ensembleSubmitting}
    />

    <Dialog
      open={regimeRequiredOpen}
      onClose={() => setRegimeRequiredOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#1a1d2e', color: '#fff' } }}
    >
      <DialogTitle sx={{ color: '#fff' }}>Ignition regime required</DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {!selectedRegime
            ? "Please select an ignition regime first."
            : "Selected ignition regime has no ignitions — please choose a regime with at least one ignition."}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRegimeRequiredOpen(false)} variant="contained" sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' } }}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default SavedSimulationsModal;
